// Translate an agent-produced `import_prefill_result` payload into the
// BafaState shape used by <BafaWizard>. Mirrors `applyBafaPrefill` from the
// legacy `web/public/app.js`.

import type { BafaState, Form1State, Form2State } from "./state";

type Raw = Record<string, unknown> | undefined;

function pickString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return undefined;
}

function toIsoDate(value: unknown): string | undefined {
  const s = pickString(value);
  if (!s) return undefined;
  // Accept YYYY-MM-DD verbatim. For other inputs, try Date.parse and format.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return undefined;
  const d = new Date(t);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeMitarbeitende(value: unknown): string | undefined {
  const s = pickString(value);
  if (!s) return undefined;
  // Strip trailing units / words so "12 Mitarbeitende" → "12".
  const match = s.match(/^\s*(\d+(?:[.,]\d+)?)/);
  return match ? match[1] : s;
}

function asObject(value: unknown): Raw {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

export function applyBafaPrefill(prev: BafaState, data: Record<string, unknown>): BafaState {
  const form1Source = asObject(data.form1) ?? asObject(data.step1) ?? data;
  const form2Source = asObject(data.form2) ?? asObject(data.step2) ?? asObject(data.unternehmen) ?? data;

  const f1: Partial<Form1State> = {};
  const websiteUrl = pickString(form1Source.websiteUrl, form1Source.website_url);
  if (websiteUrl) f1.websiteUrl = websiteUrl;
  const mitarbeitende = normalizeMitarbeitende(form1Source.mitarbeitende);
  if (mitarbeitende) f1.mitarbeitende = mitarbeitende;
  const kernproblem = pickString(form1Source.kernproblem, form1Source.kernproblem_anlass);
  if (kernproblem) f1.kernproblem = kernproblem;
  const zeitraumStart = toIsoDate(form1Source.zeitraumStart ?? form1Source.zeitraum_start);
  if (zeitraumStart) f1.zeitraumStart = zeitraumStart;
  const zeitraumEnd = toIsoDate(form1Source.zeitraumEnd ?? form1Source.zeitraum_end);
  if (zeitraumEnd) f1.zeitraumEnd = zeitraumEnd;
  const transkripte = pickString(form1Source.transkripte);
  if (transkripte) f1.transkripte = transkripte;
  const muendliche = pickString(form1Source.muendlicheAbsprachen, form1Source.muendliche_absprachen);
  if (muendliche) f1.muendlicheAbsprachen = muendliche;

  const f2: Partial<Form2State> = {};
  const firmenname = pickString(form2Source.firmenname, form2Source.name);
  if (firmenname) f2.firmenname = firmenname;
  const branche = pickString(form2Source.branche);
  if (branche) f2.branche = branche;
  const inhaber = pickString(form2Source.inhaber);
  if (inhaber) f2.inhaber = inhaber;
  const standort = pickString(form2Source.standort);
  if (standort) f2.standort = standort;

  const maxReachedStep = (prev.maxReachedStep < 2 ? 2 : prev.maxReachedStep) as BafaState["maxReachedStep"];

  return {
    ...prev,
    phase: "wizard",
    form1: { ...prev.form1, ...f1 },
    form2: { ...prev.form2, ...f2 },
    step: 1,
    maxReachedStep,
  };
}
